// UserMenu — bouton avatar + dropdown contenant l'identité, le service et la déconnexion
// Utilisé dans le header (App.jsx). Pattern moderne SaaS (Notion / Linear / Vercel).
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, LogOut, User } from 'lucide-react';

const UserMenu = ({ user, onLogout, services = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fermeture au clic extérieur ou Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (!user) return null;

  const serviceName = (services.find(s => s.id === user.service_id) || {}).name || null;
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 p-1 rounded-full hover:bg-elevated-1 transition-colors min-h-[40px]"
        aria-label={`Menu utilisateur de ${fullName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={fullName}
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {user.avatar}
        </div>
        <ChevronDown
          className={`hidden sm:block w-4 h-4 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 z-50 w-64 bg-elevated-2 border border-border-subtle rounded-xl shadow-xl overflow-hidden"
        >
          {/* En-tête identité */}
          <div className="px-4 py-3 border-b border-border-subtle bg-elevated-1">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                {user.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">{fullName}</p>
                <p className="text-xs text-text-muted truncate">
                  {user.role_name}
                  {serviceName && <> · {serviceName}</>}
                </p>
                {user.email && (
                  <p className="text-[11px] text-text-muted truncate mt-0.5">{user.email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section actions */}
          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors text-left min-h-[40px]"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
